import DigiobcanApi from '../api/DigiobcanApi';
import {
  AI_DECISION_TYPE_LABEL,
  type AiDecisionType,
} from '../lib/aiDecisions';
import { useM } from '../lib/query';
import { useLogger } from '../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../utils';
import ConfirmationDialog from './ConfirmationDialog';
import { Checkbox, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';
import { useNavigate } from 'react-router';

/** Podnet vybratý v zozname. Na skúšobný beh stačí identifikátor. */
export type AiDryRunReport = {
  reportId: string;
  title: string;
};

type Props = {
  /** Prázdne pole = dialóg je zavretý. Rovnaká dohoda ako v archivácii. */
  reports: AiDryRunReport[];
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * Horná hranica výberu - rovnaká ako `maxItems` v API. Analyzér behy púšťa
 * po jednom a na zdieľanej GPU trvá podnet s fotkami desiatky sekúnd, takže
 * viac naraz by sa čakalo desiatky minút.
 */
export const AI_DRY_RUN_MAX = 50;

/** „1 podnet" / „3 podnety" / „7 podnetov" - slovenské skloňovanie. */
const reportsLabel = (count: number) => {
  if (count === 1) return __('1 podnet');
  if (count >= 2 && count <= 4) return `${count} ${__('podnety')}`;
  return `${count} ${__('podnetov')}`;
};

/**
 * Úlohy, ktoré vedia bežať naskúšku - `tasks` v `POST /admin/ai-decisions-dry-run`.
 * Duplicity medzi nimi nie sú: rozhodnutia AI ich ukazujú, ale analyzer ich
 * naskúšku nespúšťa (zapísal by návrhy, ktoré sa od ostrých nedajú odlíšiť).
 */
type DryRunTask = Exclude<AiDecisionType, 'duplicates'>;

const TASK_HINT: Record<DryRunTask, string> = {
  categories: __('Do ktorej kategórie by AI podnet zaradila.'),
  explicit: __('Či by AI podnet skryla ako nevhodný.'),
};

/**
 * Skúšobný beh AI nad podnetmi z výberu v zozname.
 *
 * Slúži na ladenie: keď úradník zmení popis kategórie, chce vidieť, ako by
 * na ňu AI zaradila podnety, ktoré už v systéme sú. Beh sa zapíše medzi
 * rozhodnutia AI so štítkom „Skúšobný" a **na podnete nezmení nič** - žiadny
 * nový nález, notifikácia, body ani viditeľnosť.
 *
 * API odpovie hneď a behy bežia v reports-analyzer po jednom, takže výsledky
 * pribúdajú postupne. Preto toast vedie na zoznam skúšobných behov, nie na
 * hotový výsledok.
 */
const AdminReportsAiDryRunModal: React.FC<Props> = ({
  reports,
  onClose,
  onSuccess,
}) => {
  const { logger } = useLogger();
  const navigate = useNavigate();
  const [tasks, setTasks] = React.useState<DryRunTask[]>(['categories']);

  const tooMany = reports.length > AI_DRY_RUN_MAX;

  const mutation = useM(
    () =>
      DigiobcanApi.adminCreateAiDecisionsDryRun(
        {},
        {
          reportIds: reports.map((report) => report.reportId),
          tasks: tasks as [DryRunTask, ...DryRunTask[]],
        },
      ),
    {
      onSuccess: ({ queued }) => {
        toastSuccess(
          `${__('Na AI vyhodnotenie odoslané:')} ${reportsLabel(queued)}`,
        );
        onSuccess();
        navigate('/administracia/ai?onlyDryRun=true');
      },
      onError: (error) => {
        logger.error('AI dry run failed', {
          action: 'aiDryRunReports',
          error,
          data: { total: reports.length, tasks },
        });
        toastError(__('Niečo sa pokazilo.'));
      },
    },
  );

  const toggle = (task: DryRunTask) =>
    setTasks((current) =>
      current.includes(task)
        ? current.filter((item) => item !== task)
        : [...current, task],
    );

  const run = () => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    mutation.mutate(undefined);
  };

  return (
    <ConfirmationDialog
      open={reports.length > 0}
      title={`${__('AI vyhodnotenie')}: ${reportsLabel(reports.length)}`}
      description={__(
        'Skúšobný beh - AI podnety posúdi a výsledok uvidíte v rozhodnutiach AI, ale na podnetoch sa nič nezmení. Výsledky pribúdajú postupne, podnet s fotkami trvá desiatky sekúnd.',
      )}
      confirmLabel={__('Spustiť')}
      confirmDisabled={!tasks.length || tooMany}
      isLoading={mutation.isPending}
      onConfirm={run}
      onClose={onClose}
    >
      <Stack gap={1}>
        {(Object.keys(TASK_HINT) as DryRunTask[]).map((task) => (
          <Stack
            key={task}
            component='label'
            direction='row'
            gap={1}
            alignItems='flex-start'
            sx={{ cursor: 'pointer' }}
          >
            <Checkbox
              checked={tasks.includes(task)}
              onChange={() => toggle(task)}
              sx={{ p: 0, mt: 0.25 }}
            />
            <Stack gap={0}>
              <Typography variant='body2' fontWeight={600}>
                {AI_DECISION_TYPE_LABEL[task]}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {TASK_HINT[task]}
              </Typography>
            </Stack>
          </Stack>
        ))}
        {tooMany ? (
          <Typography variant='body2' color='error'>
            {`${__('Naraz sa dá poslať najviac')} ${reportsLabel(AI_DRY_RUN_MAX)}.`}
          </Typography>
        ) : null}
      </Stack>
    </ConfirmationDialog>
  );
};

export default AdminReportsAiDryRunModal;
